// ClosureDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Button, 
  Tabs, 
  Descriptions, 
  Table, 
  Space, 
  Popconfirm, 
  message, 
  Upload, 
  Form, 
  Input,
  Select,
  Spin
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  DownloadOutlined, 
  UploadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { getClosureDetails, UpdateClosuresService, deleteClosureDetails} from '../../../services/ClosureService';
import axios from 'axios';
import {getDropdownByName, getDropdownChildren} from '../../../services/DropdownService';
import { GetLicenseNoService } from '../../../services/FileDataService';
import {SERVER_URL} from "../../../const";

const { TabPane } = Tabs;
const { Option } = Select;

// Constants
const FORM_TYPES = ['CLSR_ADV_AUTH', 'CLSR_EPCG', 'CLSR_BOND'];
const EXCEL_TYPES = ['CLSR_DRAWBACK', 'CLSR_RODTEP'];
const CLSR_STATUS_DROPDOWN_NAME = 'closure_status';
const CLSR_REMARK_DROPDOWN_NAME = 'closure_remark';

const ClosureDetailsModal = ({ 
  visible, 
  onClose, 
  closureId, 
  closureType, 
  iec,
  onDelete 
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [closureData, setClosureData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();
  
  // Document states
  const [fileList, setFileList] = useState([]);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [remarkOptions, setRemarkOptions] = useState([]);
  const [licenseOptions, setLicenseOptions] = useState([]);
   
  // For liability data (DRAWBACK/RODTEP)
  const [liabilityFile, setLiabilityFile] = useState(null);
  const [editingLiabilityRecord, setEditingLiabilityRecord] = useState(null);

  // Status options (sample data - replace with your actual data)
  const getClosureFormDropdown = async () => {
        const response = await getDropdownByName(CLSR_STATUS_DROPDOWN_NAME);

        if(response){
            console.log("dropdown response: ", response);
            var statusDropdown = [];
            response.forEach(item => {
                const option = {
                    id: item.dropdown_id,
                    value: item.option_value,
                    label: item.option_label
                }

                statusDropdown.push(option);
            })

            setStatusOptions(statusDropdown);
        }
    }
  

  // Get closure details on mount or when closureId changes
  useEffect(() => {
    if (visible && closureId) {
      fetchClosureDetails();
      getClosureFormDropdown();
      setDocumentsToDelete([]);
      setEditMode(false);
      fetchLicenseNumbers(iec);
    }
  }, [visible, closureId]);

const fetchLicenseNumbers = async (iec) => {
    try {
        const token = localStorage.getItem('token');
        const response = await GetLicenseNoService(token ,iec, closureType);
        if (response && response.data) {
          const options = response.data.map((license_no, index) => ({
              value: license_no,
              label: license_no,
              key: index,
          }));
          setLicenseOptions(options);
        }
    } catch (error) {
        console.error('Error fetching license numbers:', error);
    }
};

  const fetchClosureDetails = async () => {
    setLoading(true);
    try {
      const response = await getClosureDetails(closureId);
      setClosureData(response);
      
      // Set form values if form type
      if (FORM_TYPES.includes(closureType) && response.data.form_data) {
        setSelectedStatus(response.data.form_data.status);
        form.setFieldsValue({
          formData: response.data.form_data
        });
      }
      
      // Set documents
      if (response.documents) {
        // Format documents for antd Upload component
        const docs = response.documents.map(doc => ({
          uid: doc.closure_doc_id,
          name: doc.original_filename,
          status: 'done',
          url: doc.document_path,
          // Store the full document object for reference
          document: doc
        }));
        setFileList(docs);
      }
    } catch (error) {
      console.error('Error fetching closure details:', error);
      message.error('Failed to load closure details');
    } finally {
      setLoading(false);
    }
  };

  // Handle form data update
  const handleFormDataUpdate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const formData = new FormData();
      
      // Add form data
      if (FORM_TYPES.includes(closureType)) {
        formData.append('form_data', JSON.stringify(values.formData));
      }
      
      // Add documents to delete
      if (documentsToDelete.length > 0) {
        formData.append('documents_to_delete', JSON.stringify(documentsToDelete));
      }
      
      // Add new documents
      if (fileList.filter(file => file.originFileObj).length > 0) {
        fileList.forEach(file => {
          if (file.originFileObj) {
            formData.append('new_documents', file.originFileObj);
          }
        });
      }
      
      // For DRAWBACK/RODTEP if updating liability file
      if (EXCEL_TYPES.includes(closureType) && liabilityFile) {
        formData.append('liability_file', liabilityFile);
      }

      const response = await UpdateClosuresService(closureId, formData);
      
      message.success('Closure updated successfully');
      setEditMode(false);
      fetchClosureDetails(); // Refresh data
    } catch (error) {
      console.error('Error updating closure:', error);
      message.error('Failed to update closure');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete closure
  const handleDeleteClosure = async () => {
    try {
      setLoading(true);
      const response  = await deleteClosureDetails(closureId);
      setLoading(false);
      if (response){
        message.success('Closure deleted successfully');
      }
      else{
        message.error('Failed to delete closure');
      }
      onClose();
      if (onDelete) onDelete(closureId);
    } catch (error) {
      console.error('Error deleting closure:', error);
      message.error('Failed to delete closure');
    } finally {
      setLoading(false);
    }
  };

  // Document related handlers
  const handleDocumentPreview = async (file) => {
    // Open document in new tab or download
    if (file.url) {
      console.log("file", file.url);
      const filename = file.url.split('/').at(-1);
      const token = localStorage.getItem("token"); 
      try {
        const response = await fetch(`${SERVER_URL}/file/preview?filepath=${file.url}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to download file");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error("Download failed:", error);
      }

    }
  };

  const handleDocumentChange = ({ fileList: newFileList }) => {
    console.log("newFileList: ", newFileList)
    setFileList(newFileList);
  };

  const handleDocumentRemove = (file) => {
    // If file has uid (existing document), add to delete list
    if (file.uid && !file.originFileObj) {
      setDocumentsToDelete([...documentsToDelete, file.uid]);
    }
    return true; // Allow removal
  };

  // Liability data table columns for DRAWBACK/RODTEP
  const getLiabilityColumns = () => [
    { title: 'SHB No', dataIndex: 'shb_no', key: 'shb_no', width: 120 },
    { title: 'SHB Date', dataIndex: 'shb_date', key: 'shb_date', width: 120 },
    { title: 'Port', dataIndex: 'port', key: 'port', width: 100 },
    { title: 'Challan No', dataIndex: 'challan_no', key: 'challan_no', width: 120 },
    { title: 'Challan Date', dataIndex: 'challan_date', key: 'challan_date', width: 120 },
    { title: 'Challan Amt.', dataIndex: 'challan_amount', key: 'challan_amount', width: 120 },
    { title: 'Liability Amt. Paid', dataIndex: 'liability_amount_paid', key: 'liability_amount_paid', width: 150 },
    { title: 'Remark', dataIndex: 'remark', key: 'remark', width: 150 },
    // {
    //   title: 'Actions',
    //   key: 'actions',
    //   render: (_, record) => (
    //     <Space>
    //       <Button 
    //         icon={<EditOutlined />} 
    //         size="small" 
    //         onClick={() => setEditingLiabilityRecord(record)}
    //       />
    //       <Popconfirm
    //         title="Are you sure you want to delete this record?"
    //         onConfirm={() => handleDeleteLiabilityRecord(record.id)}
    //         okText="Yes"
    //         cancelText="No"
    //       >
    //         <Button icon={<DeleteOutlined />} size="small" danger />
    //       </Popconfirm>
    //     </Space>
    //   )
    // }
  ];

  // Delete liability record
  const handleDeleteLiabilityRecord = async (recordId) => {
    try {
      setLoading(true);
      await axios.delete(`/api/closures/${closureId}/liability/${recordId}`);
      message.success('Record deleted successfully');
      fetchClosureDetails(); // Refresh data
    } catch (error) {
      console.error('Error deleting record:', error);
      message.error('Failed to delete record');
    } finally {
      setLoading(false);
    }
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    setDocumentsToDelete([]);
    fetchClosureDetails(); // Refresh data
  };

  // Render form fields for ADV_AUTH, EPCG, and BOND
  const renderFormFields = () => {
    if (!FORM_TYPES.includes(closureType)) return null;

    switch (closureType) {
      case 'CLSR_ADV_AUTH':
        return (
          <>
            <Form.Item 
              name={['formData', 'license_no']} 
              label="License Number"
              rules={[{ required: true, message: 'Please enter license number' }]}
            >
              <Input disabled={!editMode} />
            </Form.Item>
            <Form.Item 
              name={['formData', 'authorizedValue']} 
              label="Authorized Value"
              rules={[{ required: true, message: 'Please enter authorized value' }]}
            >
              <Input type="number" disabled={!editMode} />
            </Form.Item>
          </>
        );
      case 'EPCG':
        return (
          <>
            <Form.Item 
              name={['formData', 'license_no']} 
              label="EPCG Number"
              rules={[{ required: true, message: 'Please enter EPCG number' }]}
            >
              <Input disabled={!editMode} />
            </Form.Item>
            <Form.Item 
              name={['formData', 'dutyAmount']} 
              label="Duty Amount"
              rules={[{ required: true, message: 'Please enter duty amount' }]}
            >
              <Input type="number" disabled={!editMode} />
            </Form.Item>
          </>
        );
      case 'BOND':
        return (
          <>
            <Form.Item 
              name={['formData', 'bondNumber']} 
              label="Bond Number"
              rules={[{ required: true, message: 'Please enter bond number' }]}
            >
              <Input disabled={!editMode} />
            </Form.Item>
            <Form.Item 
              name={['formData', 'bondValue']} 
              label="Bond Value"
              rules={[{ required: true, message: 'Please enter bond value' }]}
            >
              <Input type="number" disabled={!editMode} />
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  const handleLicenseChange = (value) => {
    form.setFieldsValue({
      formData: {
        ...form.getFieldValue('formData'),
        license_no: value,  // Update the selected license number
      },
    });
  };
  
  const handleStatusChange = async (value, option) => {
          console.log("option: ", option)
          const parentId = option.id;
          setSelectedStatus(value);
  
          // Reset both the form field and the Select component's value
          form.setFieldsValue({
              formData: {
                  ...form.getFieldValue('formData'),
                  remark: undefined
              }
          });
          setRemarkOptions([]);
  
          // Populate children remark dropdown
          const children = await getDropdownChildren(CLSR_REMARK_DROPDOWN_NAME, parentId);
          var remarkDropdown = [];
          await children.forEach(child => {
              const option = {
                  id: child.dropdown_id,
                  value: child.option_value,
                  label: child.option_label
              }
              remarkDropdown.push(option);
          })
  
          setRemarkOptions(remarkDropdown)
          
    };

  // Common form fields for all FORM_TYPES
  const renderCommonFormFields = () => {

    if (!FORM_TYPES.includes(closureType)) return null;
    
    return (
      <>
        <Form.Item 
              name={['formData', 'license_no']} 
              label="License Number"
              rules={[{ required: true, message: 'Please enter license number' }]}
            >
              <Select 
                disabled={!editMode} 
                onChange={handleLicenseChange}
              >
                {licenseOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
        </Form.Item>
        <Form.Item
          name={['formData', 'status']}
          label="Status"
          rules={[{ required: true, message: 'Please select status' }]}
        >
          <Select disabled={!editMode} onChange={handleStatusChange}>
            {statusOptions.map(option => (
              <Option key={option.value} value={option.value} id={option.id}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        {/* {!editMode && 
        <Form.Item
          name={['formData', 'remark']}
          label="Remark"
        >
          <Input disabled={!editMode} />
        </Form.Item>
        } */}
        

        {selectedStatus && (
            <Form.Item
            name={['formData', 'remark']}
            label="Remark"
            rules={[{ required: true, message: 'Please select remark' }]}
            >
                <Select disabled={!editMode} placeholder="Select Remark">
                    {remarkOptions?.map(option => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                    ))}
                </Select>
            </Form.Item>
        )}
        
        <Form.Item name={['formData', 'param1']} label="Textbox 1">
          <Input disabled={!editMode} />
        </Form.Item>
        
        <Form.Item name={['formData', 'param2']} label="Textbox 2">
          <Input disabled={!editMode} />
        </Form.Item>
        
        <Form.Item name={['formData', 'param3']} label="Textbox 3">
          <Input disabled={!editMode} />
        </Form.Item>
        
        <Form.Item name={['formData', 'param4']} label="Textbox 4">
          <Input disabled={!editMode} />
        </Form.Item>
      </>
    );
  };

  // Upload props for documents
  const uploadProps = {
    onRemove: handleDocumentRemove,
    beforeUpload: file => {
      // setFileList([...fileList, { 
      //   uid: `new-${file.uid}`, 
      //   name: file.name, 
      //   status: 'done',
      //   originFileObj: file
      // }]);
      return false;
    },
    fileList,
    onChange: handleDocumentChange,
    onPreview: handleDocumentPreview,
    disabled: !editMode
  };

  // Upload props for liability file
  const liabilityUploadProps = {
    onRemove: () => {
      setLiabilityFile(null);
    },
    beforeUpload: file => {
      setLiabilityFile(file);
      return false;
    },
    fileList: liabilityFile ? [liabilityFile] : [],
    disabled: !editMode
  };

  // Modal title based on closure type
  const getModalTitle = () => {
    return `${closureType && closureType.replace(/CLSR_/g, "").replace(/_/g, " ")} Closure Details`;
  };

  return (
    <Modal
      title={"IEC: " + iec + " - " + getModalTitle()}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this closure?"
          onConfirm={handleDeleteClosure}
          okText="Yes"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete Closure
          </Button>
        </Popconfirm>,
        !editMode ? (
          <Button
            key="edit"
            type="primary"
            className="cip-btn-submit"
            icon={<EditOutlined />}
            onClick={() => setEditMode(true)}
          >
            Edit
          </Button>
        ) : (
          <>
            <Button key="cancel" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button
              key="save"
              type="primary"
              className="cip-btn-submit"
              onClick={handleFormDataUpdate}
              loading={loading}
            >
              Save
            </Button>
          </>
        ),
      ]}
    >
      {loading && !closureData ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Tab 1: Form Data (for ADV_AUTH, EPCG, BOND) or Liability Data (for DRAWBACK, RODTEP) */}
          <TabPane tab="Details" key="1">
            {FORM_TYPES.includes(closureType) ? (
              <Form
                form={form}
                layout="vertical"
              >
                {/* {renderFormFields()} */}
                {renderCommonFormFields()}
              </Form>
            ) : (
              <>
                {/* Liability Data Table for DRAWBACK/RODTEP */}
                {closureData?.data?.liability_data && (
                  <>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                      <h4>Liability Data</h4>
                      {editMode && (
                        <Upload {...liabilityUploadProps}>
                          <Button icon={<UploadOutlined />}>Upload New Liability File</Button>
                        </Upload>
                      )}
                    </div>
                    {closureData?.data?.file_status?.filename.split('_')[2]} ({closureData?.data?.file_status?.status}) ({closureData?.data?.file_status?.remarks})
                    <Table
                      dataSource={closureData.data.liability_data}
                      columns={getLiabilityColumns()}
                      size="small"
                      rowKey="id"
                      scroll={{ x: 'max-content' }}
                    />
                  </>
                )}
              </>
            )}
          </TabPane>
          
          {/* Tab 2: Documents (for all closure types) */}
          <TabPane tab="Documents" key="2">
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <h3>Supporting Documents</h3>
              {editMode && (
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Upload New Document</Button>
                </Upload>
              )}
            </div>
            
            <Table
              dataSource={fileList}
              columns={[
                {
                  title: 'Document Name',
                  dataIndex: 'name',
                  key: 'name',
                  width: 500,
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 120,
                  render: (_, record) => (
                    <Space>
                      <Button 
                        icon={<EyeOutlined />} 
                        size="small"
                        onClick={() => handleDocumentPreview(record)}
                      />
                      <Button 
                        icon={<DownloadOutlined />} 
                        size="small"
                        onClick={() => handleDocumentPreview(record)}
                      />
                      
                    </Space>
                  ),
                },
              ]}
              rowKey="uid"
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </TabPane>
        </Tabs>
      )}
    </Modal>
  );
};

export default ClosureDetailsModal;

// Usage example:
// import ClosureDetailsModal from './ClosureDetailsModal';
// 
// function YourComponent() {
//   const [isModalVisible, setIsModalVisible] = useState(false);
//   
//   return (
//     <>
//       <Button onClick={() => setIsModalVisible(true)}>
//         View Closure Details
//       </Button>
//       
//       <ClosureDetailsModal
//         visible={isModalVisible}
//         onClose={() => setIsModalVisible(false)}
//         closureId={123} // The closure ID
//         closureType="ADV_AUTH" // The closure type
//         onDelete={(id) => console.log(`Closure ${id} deleted`)}
//       />
//     </>
//   );
// }